import { MoreHorizontal } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import type { RecordGroup } from '@/types/api/record-group'
import { cn } from '@/utils/cn'

import { useGroupSections } from './useGroupSections'

/**
 * Section tabs for places the group sidebar (which lists the same sections) is not shown:
 * phones, and the form designer below xl.
 */
export function RecordGroupSectionNav({ group, hideOnMobile = false }: { group: RecordGroup | null; hideOnMobile?: boolean }) {
  const sections = useGroupSections(group)
  const primaryMobileTabs = sections.filter(({ value }) => !['dashboard', 'settings'].includes(value))
  const moreTabs = sections.filter(({ value }) => ['dashboard', 'settings'].includes(value))

  return (
    <header className={`${hideOnMobile ? 'hidden md:block xl:hidden' : 'block md:hidden'} sticky top-0 z-20 shrink-0 border-b border-border bg-background`}>
      <nav aria-label="Record group sections" className="hidden h-11 items-stretch gap-1 overflow-x-auto px-3 md:flex">
        {sections.map(({ label, value, icon: Icon, to, active: isActive }) => {

          return (
            <Link
              key={value}
              to={to}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'inline-flex shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground',
                isActive ? 'border-primary text-primary' : 'border-transparent',
              )}
            >
              <Icon aria-hidden="true" className="h-4 w-4" />
              {label}
            </Link>
          )
        })}
      </nav>
      <nav aria-label="Record group sections" className={`${hideOnMobile ? 'hidden' : 'grid'} min-h-[4.25rem] grid-cols-5 items-stretch md:hidden`}>
        {primaryMobileTabs.map(({ label, value, icon: Icon, to, active: isActive }) => {
          const mobileLabel = value === 'form' ? 'Form' : label
          return (
            <Link
              key={value}
              to={to}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'flex min-w-0 flex-col items-center justify-center gap-1 border-b-2 px-1 py-2 text-[11px] font-medium text-muted-foreground',
                isActive ? 'border-primary text-primary' : 'border-transparent',
              )}
            >
              <Icon aria-hidden="true" className="h-5 w-5 shrink-0" />
              <span className="max-w-full truncate">{mobileLabel}</span>
            </Link>
          )
        })}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className={cn(
                'h-auto min-w-0 flex-col gap-1 rounded-none border-b-2 px-1 py-2 text-[11px] font-medium text-muted-foreground',
                moreTabs.some(({ active }) => active) ? 'border-primary text-primary' : 'border-transparent',
              )}
              aria-label="More record group sections"
            >
              <MoreHorizontal aria-hidden="true" className="h-5 w-5" />
              More
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {moreTabs.map(({ label, value, icon: Icon, to, active }) => (
              <DropdownMenuItem key={value} asChild>
                <Link to={to} aria-current={active ? 'page' : undefined}>
                  <Icon aria-hidden="true" />
                  {label}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </nav>
    </header>
  )
}
